import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './BecomeInstructor.css';
import usePricing from '../hooks/usePricing';


const faqs = [
  { q: 'Do I need my own car?', a: 'Yes. Instructors must provide their own vehicle that meets driving instruction standards, including dual controls, valid insurance for driving instruction, and a valid MOT where required.' },
  { q: 'Can I work part-time?', a: 'Absolutely. Many instructors work part-time around other commitments. You set your own schedule.' },
  { q: 'Can PDIs register on the platform?', a: 'At the moment we are only accepting fully qualified ADIs with a green badge who are authorised to provide driving instruction.' },
  { q: 'When do I get paid?', a: 'Students pay the upfront booking fee online through our platform to secure the lesson. After the first lesson, the remaining payment is collected directly by the instructor from the student.' },
  { q: 'Am I employed or self-employed?', a: 'All our instructors are self-employed independent contractors. You\'re responsible for your own tax affairs.' },
];

const BENEFITS = [
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="6" y1="42" x2="42" y2="42"/>
        <line x1="6" y1="42" x2="6" y2="6"/>
        <polyline points="10,38 18,30 28,34 40,12"/>
        <circle cx="10" cy="38" r="2.5" fill="currentColor" stroke="none"/>
        <circle cx="18" cy="30" r="2.5" fill="currentColor" stroke="none"/>
        <circle cx="28" cy="34" r="2.5" fill="currentColor" stroke="none"/>
        <circle cx="40" cy="12" r="2.5" fill="currentColor" stroke="none"/>
        <line x1="36" y1="8" x2="40" y2="12"/>
        <line x1="36" y1="16" x2="40" y2="12"/>
      </svg>
    ),
    title: 'No Franchise Fees',
    desc: 'Unlike traditional driving schools, Fast Track Driving School does not charge franchise fees, weekly fees, or monthly charges. You remain fully independent.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="24" cy="12" r="6"/>
        <path d="M12 42c0-6.6 5.4-12 12-12s12 5.4 12 12"/>
        <circle cx="8" cy="16" r="4.5"/>
        <path d="M2 38c0-4.7 2.7-8.8 6.5-10.7"/>
        <circle cx="40" cy="16" r="4.5"/>
        <path d="M46 38c0-4.7-2.7-8.8-6.5-10.7"/>
      </svg>
    ),
    title: 'More Students in Your Area',
    desc: 'We connect you with learners actively looking for driving lessons in your local area, helping you grow your student base without spending time on marketing.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="24" cy="24" r="18"/>
        <line x1="24" y1="14" x2="24" y2="24"/>
        <line x1="24" y1="24" x2="33" y2="30"/>
        <circle cx="24" cy="6" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="24" cy="42" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="6" cy="24" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="42" cy="24" r="1.5" fill="currentColor" stroke="none"/>
      </svg>
    ),
    title: 'Full Control of Your Schedule',
    desc: 'You choose your own working hours. Teach full-time, part-time, evenings, or weekends — the choice is entirely yours.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 6h12v4H18z"/>
        <rect x="10" y="8" width="28" height="34" rx="3"/>
        <path d="M18 8h12"/>
        <path d="M15 26l5 5 11-10"/>
        <line x1="15" y1="35" x2="26" y2="35"/>
      </svg>
    ),
    title: 'We Handle Admin',
    desc: 'We manage bookings, payment collection, and customer communication. Focus 100% on teaching.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="6" y="8" width="36" height="32" rx="4"/>
        <line x1="14" y1="20" x2="34" y2="20"/>
        <line x1="14" y1="28" x2="26" y2="28"/>
        <circle cx="36" cy="34" r="6" fill="none"/>
        <path d="M33 34l2 2 4-4" strokeWidth="1.8"/>
      </svg>
    ),
    title: 'Simple & Transparent System',
    desc: 'Students pay an upfront booking fee through our platform to secure the lesson. After the first session, instructors collect the remaining lesson payments directly from the student.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="10" cy="13" r="6"/>
        <path d="M2 37c0-5 3.6-9 8-9"/>
        <circle cx="38" cy="13" r="6"/>
        <path d="M46 37c0-5-3.6-9-8-9"/>
        <line x1="19" y1="24" x2="29" y2="24"/>
        <path d="M26 20l4 4-4 4"/>
      </svg>
    ),
    title: 'Instructor Referral Bonus',
    desc: 'Know another instructor? Refer them to Fast Track Driving School using your referral code and earn Up to £50 when they complete their first driving session.',
  },
];

function BecomeInstructor() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const { pricing } = usePricing();
  const perHour = pricing.hourly_rate || 37;

  return (
    <div className="bi-page">

      {/* ============ HERO ============ */}
      <section className="bi-hero">
        <div className="bi-hero-overlay"></div>
        <div className="bi-hero-content container">
          <span className="bi-eyebrow">Join Our Nationwide Network</span>
          <h1 className="bi-hero-title">Become a Driving Instructor<br />with Fast Track</h1>
          <p className="bi-hero-subtitle">
            Join hundreds of DVSA-approved ADI instructors across the UK. We handle the admin, you focus on teaching — no franchise fees, no employment contracts.
          </p>
          <div className="bi-hero-ctas">
            <button className="bi-btn bi-btn--primary" onClick={() => navigate('/apply-signup')}>
              Start Your Application
            </button>
            <a href="#how-it-works" className="bi-btn bi-btn--outline">See How It Works</a>
          </div>
          <p className="bi-hero-login-hint">
            Already an approved instructor?{' '}
            <Link to="/instructor-login" className="bi-hero-login-link">Sign in to your portal →</Link>
          </p>
          <div className="bi-hero-trust">
            <span>✓ No monthly fees</span>
            <span>✓ DVSA-registered</span>
            <span>✓ Flexible schedule</span>
          </div>
        </div>
      </section>

      {/* ============ INCOME BANNER ============ */}
      <div className="income-banner">
        <div className="container income-banner-inner">
          <div className="income-stat">
            <span className="income-val">£{perHour}</span>
            <span className="income-lab">Per Hour Taught</span>
          </div>
          <div className="income-divider"></div>
          <div className="income-stat">
            <span className="income-val">£{perHour * 20}+</span>
            <span className="income-lab">Part-Time Weekly (20hrs)</span>
          </div>
          <div className="income-divider"></div>
          <div className="income-stat">
            <span className="income-val">£{perHour * 40}+</span>
            <span className="income-lab">Full-Time Weekly (40hrs)</span>
          </div>
          <div className="income-divider"></div>
          <div className="income-stat">
            <span className="income-val">Up to £50</span>
            <span className="income-lab">Referral Bonus Per ADI</span>
          </div>
        </div>
      </div>

      {/* ============ INSTRUCTOR LOGIN BANNER ============ */}
      <div className="bi-login-banner">
        <div className="container bi-login-banner-inner">
          <div className="bi-login-banner-text">
            <span className="bi-login-banner-label">Already with Fast Track?</span>
            <h3 className="bi-login-banner-title">Access Your Instructor Portal</h3>
            <p className="bi-login-banner-desc">View your profile, update expired documents, check your referrals and manage your account.</p>
          </div>
          <Link to="/instructor-login" className="bi-login-banner-btn">
             Instructor Login
          </Link>
        </div>
      </div>

      {/* ============ BENEFITS ============ */}
      <section className="section bi-benefits-section">
        <div className="container">
          <div className="bi-section-header">
            <span className="bi-eyebrow-dark">Why Join Fast Track?</span>
            <h2 className="bi-section-title">Why Join Fast Track Driving School</h2>
            <p className="bi-section-desc">We believe driving instructors should be rewarded, not burdened with fees and conditions.</p>
          </div>
          <div className="bi-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div className="bi-benefit-card" key={i}>
                <div className="bi-benefit-icon">{b.icon}</div>
                <h3 className="bi-benefit-title">{b.title}</h3>
                <p className="bi-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="section bi-how-section">
        <div className="container">
          <div className="bi-section-header">
            <span className="bi-eyebrow-dark">Application Process</span>
            <h2 className="bi-section-title">How to Join Fast Track</h2>
          </div>
          <div className="bi-process-timeline">
            {[
              { n: '01', title: 'Submit Your Application', desc: 'Complete our simple online instructor registration form and provide your basic details and service areas.' },
              { n: '02', title: 'Document Verification', desc: 'Our team reviews your driving instructor badge, driving licence, insurance, and vehicle documents to ensure they meet teaching standards.' },
              { n: '03', title: 'Instructor Approval', desc: 'Once your documents are verified, your profile will be approved and added to our instructor network.' },
              { n: '04', title: 'Start Receiving Student Requests', desc: 'When learners request lessons in your selected areas and transmission type, we will contact you to arrange the booking.' },
              { n: '05', title: 'Start Teaching & Earning', desc: 'After the student confirms their booking and pays the upfront fee online, you will receive their contact details and can begin lessons.' },
            ].map((s, i) => (
              <div className="bi-process-step" key={i}>
                <div className="bi-process-num">{s.n}</div>
                <div className="bi-process-content">
                  <h3 className="bi-process-title">{s.title}</h3>
                  <p className="bi-process-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REQUIREMENTS ============ */}
      <section className="section bi-requirements-section">
        <div className="container">
          <div className="bi-section-header">
            <span className="bi-eyebrow-dark">Eligibility</span>
            <h2 className="bi-section-title">Requirements to Join</h2>
          </div>
          <div className="bi-req-grid">
            <div className="bi-req-card bi-req-card--essential">
              <div className="bi-req-header">
                <span className="bi-req-icon">
                  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="28" height="28">
                    <circle cx="16" cy="16" r="13"/>
                    <path d="M10 16.5l4 4 8-8" strokeWidth="2.2"/>
                  </svg>
                </span>
                <h3>Essential Requirements</h3>
              </div>
              <ul className="bi-req-list">
                <li>Fully qualified DVSA Approved Driving Instructor (ADI – Green Badge)</li>
                <li>Full UK driving licence</li>
                <li>Right to work in the UK</li>
                <li>A suitable teaching vehicle with dual controls installed</li>
                <li>Valid motor insurance covering driving instruction (hire &amp; reward)</li>
                <li>Valid MOT certificate if the vehicle is over 3 years old</li>
              </ul>
            </div>
            <div className="bi-req-card bi-req-card--desirable">
              <div className="bi-req-header">
                <span className="bi-req-icon">
                  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="28" height="28">
                    <path d="M16 3l3.7 7.6 8.3 1.2-6 5.9 1.4 8.3L16 22l-7.4 3.9 1.4-8.3-6-5.9 8.3-1.2z"/>
                  </svg>
                </span>
                <h3>Desirable Qualities</h3>
              </div>
              <ul className="bi-req-list">
                <li>Excellent communication and teaching skills</li>
                <li>Patience and professionalism with learners</li>
                <li>Reliable timekeeping and professional attitude</li>
                <li>Ability to support nervous or beginner drivers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ REFERRAL CALLOUT ============ */}
      <section className="bi-referral-banner">
        <div className="container bi-referral-inner">
          <div className="bi-referral-text">
            <h2>💷 Know an ADI looking for work?</h2>
            <p>Refer them to <strong>Fast Track Driving School UK</strong> and earn <strong>Up to £50</strong> when they complete their first training session. No limit on referrals.</p>
          </div>
          <button className="bi-btn bi-btn--white" onClick={() => navigate('/apply-signup')}>
            Apply &amp; Start Earning
          </button>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section bi-faq-section">
        <div className="container">
          <div className="bi-section-header">
            <span className="bi-eyebrow-dark">FAQs</span>
            <h2 className="bi-section-title">Instructor Questions Answered</h2>
          </div>
          <div className="bi-faq-list">
            {faqs.map((f, i) => (
              <div className={`bi-faq-item${openFaq === i ? ' bi-faq-item--open' : ''}`} key={i}>
                <button className="bi-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <span className="bi-faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="bi-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bi-final-cta">
        <div className="container bi-final-cta-inner">
          <h2 className="bi-final-title">Ready to Join Fast Track?</h2>
          <p className="bi-final-desc">Complete our 5-step application — it takes around 15 minutes. Our team will review and respond within 48 hours.</p>
          <button className="bi-btn bi-btn--primary bi-btn--large" onClick={() => navigate('/apply-signup')}>
            Start Your Application →
          </button>
          <p className="bi-final-note">
            By applying you agree to our <Link to="/instructor-terms">Instructor Terms &amp; Conditions</Link>
          </p>
          <p className="bi-final-login">
            Already an approved instructor?{' '}
            <Link to="/instructor-login">Sign in to your Instructor Portal →</Link>
          </p>
        </div>
      </section>

    </div>
  );
}

export default BecomeInstructor;

